import { useCallback, useEffect, useRef, useState } from 'react';
import type { ControlClientMessage, ControlMessage } from '@/contracts';
import { logInfo, logWarn } from '@/lib/logger';
import {
  ROBOT_PREPARING_MESSAGE,
  ROBOT_READY_MESSAGE,
  shouldKeepReadyVisibleDuringAttemptStartHandoff,
  shouldRecoverStalledAttemptStartHandoff,
} from './attemptStartHandoff';
import {
  buildAttemptMessageMeta,
  buildAttemptResultFromMessage,
  buildAttemptWsMeta,
  buildSessionEventEntry,
  getAttemptMessageDurationSeconds,
  getAttemptResultIdempotencyKey,
  getAttemptStartIdempotencyKey,
  getAttemptWaitReadyIdempotencyKey,
  getSessionEventIdempotencyKey,
} from './teleopAttemptMessage';
import type {
  AttemptPhase,
  AttemptResult,
  BonusAttemptPrompt,
  PostAttemptPrompt,
  TeleopAttemptConfig,
  TeleopAttemptFlowState,
  TeleopAttemptWsMeta,
  TeleopSessionEventEntry,
} from './teleopAttemptTypes';

type AttemptTimeoutSource = 'active_timeout' | 'stale_start';

type PendingAttempt = {
  attemptId: string | null;
  durationSeconds: number;
};

type UseTeleopAttemptFlowParams = {
  sessionId: string;
  connected: boolean;
  debugWs: boolean;
  showDebugPanel: boolean;
  config: TeleopAttemptConfig;
  sendMessage: (message: ControlClientMessage) => boolean;
  addMessageListener: (listener: (message: ControlMessage) => void) => () => void;
  sendGrab: () => boolean;
  switchToOverhead: () => void;
  onPlayAgain?: () => void;
  onReleaseSession?: () => void | Promise<void>;
};

type UseTeleopAttemptFlowResult = TeleopAttemptFlowState & {
  handlePlayAgain: () => void;
  handleBonusAttemptOk: () => void;
  triggerGrab: () => void;
};

const ATTEMPT_RESULT_DISMISS_MS = 4500;
const MAX_PROCESSED_MESSAGE_KEYS = 64;

export const useTeleopAttemptFlow = ({
  sessionId,
  connected,
  debugWs,
  showDebugPanel,
  config,
  sendMessage,
  addMessageListener,
  sendGrab,
  switchToOverhead,
  onPlayAgain,
  onReleaseSession,
}: UseTeleopAttemptFlowParams): UseTeleopAttemptFlowResult => {
  const [attemptResult, setAttemptResult] = useState<AttemptResult | null>(null);
  const [postAttemptPrompt, setPostAttemptPrompt] =
    useState<PostAttemptPrompt | null>(null);
  const [bonusAttemptPrompt, setBonusAttemptPrompt] =
    useState<BonusAttemptPrompt | null>(null);
  const [attemptPhase, setAttemptPhase] = useState<AttemptPhase>('waiting');
  const [attemptIndex, setAttemptIndex] = useState(1);
  const [phaseEndsAt, setPhaseEndsAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [awaitingResult, setAwaitingResult] = useState(false);
  const [currentAttemptId, setCurrentAttemptId] = useState<string | null>(null);
  const [readyMessage, setReadyMessage] = useState<string | null>(null);
  const [countdownValue, setCountdownValue] = useState<number | null>(null);
  const [lastWsMeta, setLastWsMeta] =
    useState<TeleopAttemptWsMeta | null>(null);
  const [sessionEventLog, setSessionEventLog] = useState<
    TeleopSessionEventEntry[]
  >([]);

  const attemptTimeoutRef = useRef<number | null>(null);
  const resultTimeoutRef = useRef<number | null>(null);
  const bonusPromptTimeoutRef = useRef<number | null>(null);
  const attemptStartHandoffTimeoutRef = useRef<number | null>(null);
  const awaitingResultRef = useRef(false);
  const grabIssuedRef = useRef(false);
  const readySentRef = useRef(false);
  const attemptSeqRef = useRef(0);
  const countdownIntervalRef = useRef<number | null>(null);
  const countdownGoTimeoutRef = useRef<number | null>(null);
  const pendingAttemptRef = useRef<PendingAttempt | null>(null);
  const sessionIdRef = useRef(sessionId);
  const debugWsRef = useRef(debugWs);
  const showDebugPanelRef = useRef(showDebugPanel);
  const attemptPhaseRef = useRef<AttemptPhase>(attemptPhase);
  const postAttemptPromptRef = useRef<PostAttemptPrompt | null>(
    postAttemptPrompt
  );
  const bonusAttemptPromptRef = useRef<BonusAttemptPrompt | null>(
    bonusAttemptPrompt
  );
  const currentAttemptIdRef = useRef<string | null>(currentAttemptId);
  const readyMessageRef = useRef<string | null>(readyMessage);
  const attemptIndexRef = useRef(1);
  const staleTimeoutAttemptIdsRef = useRef<Set<string>>(new Set());
  const seenAttemptResultKeysRef = useRef<Set<string>>(new Set());
  const seenAttemptResultOrderRef = useRef<string[]>([]);

  const setCurrentAttemptIdWithRef = useCallback((nextAttemptId: string | null) => {
    currentAttemptIdRef.current = nextAttemptId;
    setCurrentAttemptId(nextAttemptId);
  }, []);

  const clearAttemptStartHandoffTimeout = useCallback(() => {
    const existingTimeoutId = attemptStartHandoffTimeoutRef.current;

    if (existingTimeoutId === null) {
      return;
    }

    window.clearTimeout(existingTimeoutId);
    attemptStartHandoffTimeoutRef.current = null;
  }, []);

  const clearCountdownTimers = useCallback(() => {
    const countdownIntervalId = countdownIntervalRef.current;

    if (countdownIntervalId !== null) {
      window.clearInterval(countdownIntervalId);
      countdownIntervalRef.current = null;
    }

    const countdownGoTimeoutId = countdownGoTimeoutRef.current;

    if (countdownGoTimeoutId !== null) {
      window.clearTimeout(countdownGoTimeoutId);
      countdownGoTimeoutRef.current = null;
    }
  }, []);

  const sendOperatorReady = useCallback(
    (targetSessionId: string): boolean => {
      if (!connected) {
        return false;
      }

      const operatorReadyKey = `operator_ready:${targetSessionId}`;
      const operatorReadyMessage: ControlClientMessage = {
        handler: 'operator',
        type: 'operator_ready',
        session_id: targetSessionId,
        ...buildAttemptMessageMeta(operatorReadyKey),
      };
      const sent = sendMessage(operatorReadyMessage);

      if (!sent) {
        return false;
      }

      readySentRef.current = true;

      return true;
    },
    [connected, sendMessage]
  );

  const recoverStalledAttemptStartHandoff = useCallback(() => {
    const handoffState = {
      attemptPhase: attemptPhaseRef.current,
      awaitingResult: awaitingResultRef.current,
      readyMessage: readyMessageRef.current,
    };
    const shouldRecover =
      shouldRecoverStalledAttemptStartHandoff(handoffState);

    if (!shouldRecover) {
      return;
    }

    const activeSessionId = sessionIdRef.current;

    readySentRef.current = false;
    setReadyMessage(ROBOT_PREPARING_MESSAGE);

    let retrySent = false;

    if (activeSessionId) {
      retrySent = sendOperatorReady(activeSessionId);
    }

    if (retrySent) {
      setReadyMessage('Robot getting ready');
    }

    logWarn(
      'teleop.attempt_start.recovered',
      'attempt_start handoff stalled; retrying operator_ready',
      {
        retrySent,
        sessionId: activeSessionId,
        timeoutMs: config.attemptStartHandoffTimeoutMs,
      }
    );
  }, [config.attemptStartHandoffTimeoutMs, sendOperatorReady]);

  const scheduleAttemptStartHandoffRecovery = useCallback(() => {
    clearAttemptStartHandoffTimeout();

    const nextTimeoutId = window.setTimeout(() => {
      attemptStartHandoffTimeoutRef.current = null;
      recoverStalledAttemptStartHandoff();
    }, config.attemptStartHandoffTimeoutMs);

    attemptStartHandoffTimeoutRef.current = nextTimeoutId;
  }, [
    clearAttemptStartHandoffTimeout,
    config.attemptStartHandoffTimeoutMs,
    recoverStalledAttemptStartHandoff,
  ]);

  const markAttemptResultProcessed = useCallback((idempotencyKey: string) => {
    const hasSeenMessage = seenAttemptResultKeysRef.current.has(idempotencyKey);

    if (hasSeenMessage) {
      return false;
    }

    seenAttemptResultKeysRef.current.add(idempotencyKey);
    seenAttemptResultOrderRef.current.push(idempotencyKey);
    const processedKeyCount = seenAttemptResultOrderRef.current.length;
    const exceededMaxKeys = processedKeyCount > MAX_PROCESSED_MESSAGE_KEYS;

    if (!exceededMaxKeys) {
      return true;
    }

    const evictedKey = seenAttemptResultOrderRef.current.shift();

    if (!evictedKey) {
      return true;
    }

    seenAttemptResultKeysRef.current.delete(evictedKey);

    return true;
  }, []);

  const showAttemptResult = useCallback((nextAttemptResult: AttemptResult) => {
    attemptSeqRef.current += 1;
    const nextAttemptResultWithSequence = {
      ...nextAttemptResult,
      seq: attemptSeqRef.current,
    };
    const existingAttemptTimeoutId = attemptTimeoutRef.current;

    setAttemptResult(nextAttemptResultWithSequence);

    if (existingAttemptTimeoutId !== null) {
      window.clearTimeout(existingAttemptTimeoutId);
    }

    attemptTimeoutRef.current = window.setTimeout(() => {
      attemptTimeoutRef.current = null;
      setAttemptResult(null);
    }, ATTEMPT_RESULT_DISMISS_MS);
  }, []);

  const startAttemptFromPending = useCallback(() => {
    const pendingAttempt = pendingAttemptRef.current;

    if (!pendingAttempt) {
      return;
    }

    const now = Date.now();
    const attemptEndTimestamp = now + pendingAttempt.durationSeconds * 1000;
    const roundedSecondsLeft = Math.max(
      0,
      Math.ceil(pendingAttempt.durationSeconds)
    );

    setAttemptPhase('attempt');
    setPhaseEndsAt(attemptEndTimestamp);
    setSecondsLeft(roundedSecondsLeft);
    setAwaitingResult(false);
    awaitingResultRef.current = false;
    grabIssuedRef.current = false;
    setCurrentAttemptIdWithRef(pendingAttempt.attemptId);
    setReadyMessage(null);
    setCountdownValue(null);
    pendingAttemptRef.current = null;
  }, [setCurrentAttemptIdWithRef]);

  const beginCountdown = useCallback(
    (durationSeconds: number, attemptId: string | null) => {
      clearCountdownTimers();
      clearAttemptStartHandoffTimeout();

      const pendingAttempt = {
        attemptId,
        durationSeconds,
      };

      pendingAttemptRef.current = pendingAttempt;
      setAttemptPhase('countdown');
      setReadyMessage('Get ready');
      setPhaseEndsAt(null);
      setSecondsLeft(0);
      setCountdownValue(config.countdownSeconds);
      setAwaitingResult(false);
      awaitingResultRef.current = false;
      grabIssuedRef.current = false;
      setCurrentAttemptIdWithRef(attemptId);

      countdownIntervalRef.current = window.setInterval(() => {
        setCountdownValue((previousCountdownValue) => {
          if (previousCountdownValue === null) {
            return previousCountdownValue;
          }

          const shouldStartAttempt = previousCountdownValue <= 1;

          if (!shouldStartAttempt) {
            return previousCountdownValue - 1;
          }

          clearCountdownTimers();
          setCountdownValue(0);
          countdownGoTimeoutRef.current = window.setTimeout(() => {
            countdownGoTimeoutRef.current = null;
            startAttemptFromPending();
          }, config.countdownGoMs);

          return 0;
        });
      }, 1000);
    },
    [
      clearAttemptStartHandoffTimeout,
      clearCountdownTimers,
      config.countdownGoMs,
      config.countdownSeconds,
      setCurrentAttemptIdWithRef,
      startAttemptFromPending,
    ]
  );

  const sendAttemptTimeoutForAttempt = useCallback(
    (attemptId: string, source: AttemptTimeoutSource) => {
      if (!connected) {
        return false;
      }

      const attemptTimeoutKey =
        `attempt_timeout:${sessionId}:${attemptId}:${source}`;
      const attemptTimeoutMessage: ControlClientMessage = {
        handler: 'operator',
        type: 'attempt_timeout',
        session_id: sessionId,
        attempt_id: attemptId,
        ...buildAttemptMessageMeta(attemptTimeoutKey),
      };
      const sent = sendMessage(attemptTimeoutMessage);

      if (debugWs) {
        logInfo('teleop.attempt_timeout.sent', 'attempt_timeout sent', {
          attemptId,
          sent,
          sessionId,
          source,
        });
      }

      return sent;
    },
    [connected, debugWs, sendMessage, sessionId]
  );

  const sendAttemptTimeoutForAttemptRef = useRef(sendAttemptTimeoutForAttempt);

  useEffect(() => {
    sendAttemptTimeoutForAttemptRef.current = sendAttemptTimeoutForAttempt;
  }, [sendAttemptTimeoutForAttempt]);

  const sendActiveAttemptTimeout = useCallback(() => {
    if (!connected) {
      return false;
    }

    const activeAttemptId = currentAttemptIdRef.current;

    if (!activeAttemptId) {
      if (debugWs) {
        logWarn(
          'teleop.attempt_timeout.invalid',
          'attempt_timeout missing attempt_id'
        );
      }

      return false;
    }

    return sendAttemptTimeoutForAttempt(activeAttemptId, 'active_timeout');
  }, [connected, debugWs, sendAttemptTimeoutForAttempt]);

  const beginResolveAfterGrab = useCallback(() => {
    const isAlreadyAwaitingResult = awaitingResultRef.current;

    if (isAlreadyAwaitingResult) {
      return;
    }

    setAwaitingResult(true);
    awaitingResultRef.current = true;
    setAttemptPhase('waiting');
    setPhaseEndsAt(null);
    setSecondsLeft(0);

    const existingResultTimeoutId = resultTimeoutRef.current;

    if (existingResultTimeoutId !== null) {
      window.clearTimeout(existingResultTimeoutId);
    }

    resultTimeoutRef.current = window.setTimeout(() => {
      resultTimeoutRef.current = null;

      const isStillAwaitingResult = awaitingResultRef.current;

      if (!isStillAwaitingResult) {
        return;
      }

      const timeoutResult = {
        success: false,
        reason: 'timeout',
        timestamp: new Date().toISOString(),
      };
      const reachedMaxAttempts = attemptIndexRef.current >= config.maxAttempts;

      sendActiveAttemptTimeout();
      showAttemptResult(timeoutResult);
      setAwaitingResult(false);
      awaitingResultRef.current = false;
      setCurrentAttemptIdWithRef(null);
      setPhaseEndsAt(null);
      setSecondsLeft(0);
      switchToOverhead();

      if (reachedMaxAttempts) {
        setBonusAttemptPrompt(null);
        setPostAttemptPrompt(timeoutResult);
        setAttemptPhase('done');
        return;
      }

      setPostAttemptPrompt(null);
      setBonusAttemptPrompt({
        nextAttempt: Math.min(attemptIndexRef.current + 1, config.maxAttempts),
        totalAttempts: config.maxAttempts,
      });
      setAttemptPhase('waiting');
      setAttemptIndex((previousAttemptIndex) => {
        const nextAttemptIndex = Math.min(
          previousAttemptIndex + 1,
          config.maxAttempts
        );

        attemptIndexRef.current = nextAttemptIndex;

        return nextAttemptIndex;
      });
    }, config.resultResolveTimeoutMs);
  }, [
    config.maxAttempts,
    config.resultResolveTimeoutMs,
    sendActiveAttemptTimeout,
    setCurrentAttemptIdWithRef,
    showAttemptResult,
    switchToOverhead,
  ]);

  const triggerGrab = useCallback(() => {
    const isAttemptPhase = attemptPhaseRef.current === 'attempt';
    const isAwaitingResult = awaitingResultRef.current;
    const wasGrabIssued = grabIssuedRef.current;

    if (!isAttemptPhase) {
      return;
    }

    if (isAwaitingResult) {
      return;
    }

    if (wasGrabIssued) {
      return;
    }

    const sent = sendGrab();

    if (sent) {
      grabIssuedRef.current = true;
    }

    beginResolveAfterGrab();
  }, [beginResolveAfterGrab, sendGrab]);

  const finalizeBonusTimeout = useCallback(() => {
    const timeoutPrompt = {
      success: false,
      reason: 'time expired',
      timestamp: new Date().toISOString(),
    };

    setBonusAttemptPrompt(null);
    setPostAttemptPrompt(timeoutPrompt);
    setAttemptPhase('done');
    setAttemptIndex(config.maxAttempts);
    attemptIndexRef.current = config.maxAttempts;
    clearAttemptStartHandoffTimeout();
    setReadyMessage(null);
    setPhaseEndsAt(null);
    setSecondsLeft(0);
    setAwaitingResult(false);
    awaitingResultRef.current = false;
    setCurrentAttemptIdWithRef(null);
    pendingAttemptRef.current = null;
    switchToOverhead();
    void onReleaseSession?.();
  }, [
    clearAttemptStartHandoffTimeout,
    config.maxAttempts,
    onReleaseSession,
    setCurrentAttemptIdWithRef,
    switchToOverhead,
  ]);

  const handlePlayAgain = useCallback(() => {
    setPostAttemptPrompt(null);
    setBonusAttemptPrompt(null);
    setAttemptResult(null);
    setAttemptPhase('waiting');
    setAttemptIndex(1);
    attemptIndexRef.current = 1;
    staleTimeoutAttemptIdsRef.current.clear();
    setAwaitingResult(false);
    awaitingResultRef.current = false;
    setCurrentAttemptIdWithRef(null);
    clearAttemptStartHandoffTimeout();
    setReadyMessage(ROBOT_PREPARING_MESSAGE);
    setPhaseEndsAt(null);
    setSecondsLeft(0);
    setCountdownValue(null);
    pendingAttemptRef.current = null;
    grabIssuedRef.current = false;

    const existingResultTimeoutId = resultTimeoutRef.current;

    if (existingResultTimeoutId !== null) {
      window.clearTimeout(existingResultTimeoutId);
      resultTimeoutRef.current = null;
    }

    const existingBonusPromptTimeoutId = bonusPromptTimeoutRef.current;

    if (existingBonusPromptTimeoutId !== null) {
      window.clearTimeout(existingBonusPromptTimeoutId);
      bonusPromptTimeoutRef.current = null;
    }

    switchToOverhead();
    onPlayAgain?.();
  }, [
    clearAttemptStartHandoffTimeout,
    onPlayAgain,
    setCurrentAttemptIdWithRef,
    switchToOverhead,
  ]);

  const handleBonusAttemptOk = useCallback(() => {
    setBonusAttemptPrompt(null);
    switchToOverhead();

    const existingBonusPromptTimeoutId = bonusPromptTimeoutRef.current;

    if (existingBonusPromptTimeoutId !== null) {
      window.clearTimeout(existingBonusPromptTimeoutId);
      bonusPromptTimeoutRef.current = null;
    }

    if (!connected) {
      return;
    }

    if (!sessionId) {
      return;
    }

    const sent = sendOperatorReady(sessionId);

    if (!sent) {
      return;
    }

    setReadyMessage('Robot getting ready');
  }, [connected, sendOperatorReady, sessionId, switchToOverhead]);

  useEffect(() => {
    seenAttemptResultKeysRef.current.clear();
    seenAttemptResultOrderRef.current = [];
  }, [sessionId]);

  useEffect(() => {
    if (connected) {
      return;
    }

    clearAttemptStartHandoffTimeout();
    readySentRef.current = false;
  }, [clearAttemptStartHandoffTimeout, connected]);

  useEffect(() => {
    clearAttemptStartHandoffTimeout();
    setAttemptPhase('waiting');
    setAttemptIndex(1);
    attemptIndexRef.current = 1;
    staleTimeoutAttemptIdsRef.current.clear();
    setPhaseEndsAt(null);
    setSecondsLeft(0);
    setAwaitingResult(false);
    awaitingResultRef.current = false;
    setCurrentAttemptIdWithRef(null);
    setReadyMessage(null);
    setPostAttemptPrompt(null);
    setBonusAttemptPrompt(null);
    readySentRef.current = false;
    grabIssuedRef.current = false;

    const existingResultTimeoutId = resultTimeoutRef.current;

    if (existingResultTimeoutId !== null) {
      window.clearTimeout(existingResultTimeoutId);
      resultTimeoutRef.current = null;
    }

    const existingBonusPromptTimeoutId = bonusPromptTimeoutRef.current;

    if (existingBonusPromptTimeoutId !== null) {
      window.clearTimeout(existingBonusPromptTimeoutId);
      bonusPromptTimeoutRef.current = null;
    }
  }, [clearAttemptStartHandoffTimeout, sessionId, setCurrentAttemptIdWithRef]);

  useEffect(() => {
    attemptIndexRef.current = attemptIndex;
  }, [attemptIndex]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    debugWsRef.current = debugWs;
  }, [debugWs]);

  useEffect(() => {
    showDebugPanelRef.current = showDebugPanel;
  }, [showDebugPanel]);

  useEffect(() => {
    attemptPhaseRef.current = attemptPhase;
  }, [attemptPhase]);

  useEffect(() => {
    postAttemptPromptRef.current = postAttemptPrompt;
  }, [postAttemptPrompt]);

  useEffect(() => {
    bonusAttemptPromptRef.current = bonusAttemptPrompt;
  }, [bonusAttemptPrompt]);

  useEffect(() => {
    readyMessageRef.current = readyMessage;
  }, [readyMessage]);

  useEffect(() => {
    const removeListener = addMessageListener((message) => {
      if (!('type' in message)) {
        return;
      }

      const activeSessionId = sessionIdRef.current;
      const activeAttemptPhase = attemptPhaseRef.current;
      const activeAttemptId = currentAttemptIdRef.current;
      const activePostAttemptPrompt = postAttemptPromptRef.current;
      const activeBonusPrompt = bonusAttemptPromptRef.current;
      const wsDebugEnabled = debugWsRef.current;
      const messageType = message.type;

      if (showDebugPanelRef.current) {
        const nextMeta = buildAttemptWsMeta(message);

        setLastWsMeta(nextMeta);
      }

      if (messageType === 'session_event') {
        const sessionMatches =
          typeof message.session_id === 'string' &&
          message.session_id === activeSessionId;

        if (!sessionMatches) {
          return;
        }

        const sessionEventIdempotencyKey = getSessionEventIdempotencyKey(
          message,
          activeSessionId
        );
        const wasSessionEventAlreadyProcessed = !markAttemptResultProcessed(
          sessionEventIdempotencyKey
        );

        if (wasSessionEventAlreadyProcessed) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.session_event.ignored',
              'session_event ignored (duplicate idempotency_key)',
              {
                idempotencyKey: sessionEventIdempotencyKey,
              }
            );
          }

          return;
        }

        const nextSessionEventEntry = buildSessionEventEntry(message);

        setSessionEventLog((previousEntries) => {
          const nextEntries = [nextSessionEventEntry, ...previousEntries];
          const limitedEntries = nextEntries.slice(0, 6);

          return limitedEntries;
        });

        return;
      }

      if (messageType === 'attempt_wait' || messageType === 'attempt_ready') {
        const sessionMatches =
          typeof message.session_id === 'string' &&
          message.session_id === activeSessionId;

        if (!sessionMatches) {
          if (wsDebugEnabled) {
            const inboundSessionId =
              typeof message.session_id === 'string'
                ? message.session_id
                : undefined;

            logWarn(
              'teleop.attempt_ready.ignored',
              'attempt_ready/wait ignored (session mismatch)',
              {
                inboundSessionId,
                sessionId: activeSessionId,
                type: messageType,
              }
            );
          }

          return;
        }

        const attemptStateIdempotencyKey = getAttemptWaitReadyIdempotencyKey(
          message,
          activeSessionId
        );
        const wasAttemptStateAlreadyProcessed = !markAttemptResultProcessed(
          attemptStateIdempotencyKey
        );

        if (wasAttemptStateAlreadyProcessed) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_ready.ignored',
              'attempt_ready/wait ignored (duplicate idempotency_key)',
              {
                idempotencyKey: attemptStateIdempotencyKey,
                type: messageType,
              }
            );
          }

          return;
        }

        const isAttemptInProgress =
          activeAttemptPhase === 'attempt' ||
          activeAttemptPhase === 'countdown' ||
          awaitingResultRef.current;
        const isAttemptResolved =
          activeAttemptPhase === 'done' ||
          activePostAttemptPrompt !== null ||
          activeBonusPrompt !== null;

        if (isAttemptInProgress) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_ready.ignored',
              'attempt_ready/wait ignored (attempt in progress)',
              {
                attemptId: activeAttemptId,
                sessionId: message.session_id,
                type: messageType,
              }
            );
          }

          return;
        }

        if (isAttemptResolved) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_ready.ignored',
              'attempt_ready/wait ignored (attempt already resolved)',
              {
                attemptPhase: activeAttemptPhase,
                hasBonusPrompt: activeBonusPrompt !== null,
                hasPostAttemptPrompt: activePostAttemptPrompt !== null,
                type: messageType,
              }
            );
          }

          return;
        }

        if (messageType === 'attempt_ready') {
          setReadyMessage(ROBOT_READY_MESSAGE);
          scheduleAttemptStartHandoffRecovery();
          setAttemptPhase('waiting');
          return;
        }

        const waitReason =
          typeof message.reason === 'string' ? message.reason : null;
        const previousReadyMessage = readyMessageRef.current;
        const shouldKeepReadyVisible =
          shouldKeepReadyVisibleDuringAttemptStartHandoff(
            previousReadyMessage,
            waitReason,
            config.attemptWaitEnqueuedReason
          );
        const nextReadyMessage = shouldKeepReadyVisible
          ? ROBOT_READY_MESSAGE
          : ROBOT_PREPARING_MESSAGE;

        setReadyMessage(nextReadyMessage);

        if (shouldKeepReadyVisible) {
          scheduleAttemptStartHandoffRecovery();
        } else {
          clearAttemptStartHandoffTimeout();
        }

        setAttemptPhase('waiting');

        return;
      }

      if (messageType === 'attempt_start' || messageType === 'attempt_started') {
        const sessionMatches =
          typeof message.session_id === 'string' &&
          message.session_id === activeSessionId;

        if (!sessionMatches) {
          return;
        }

        if (activeBonusPrompt) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_start.ignored',
              'attempt_start ignored (bonus prompt active)'
            );
          }

          return;
        }

        if (activePostAttemptPrompt !== null || activeAttemptPhase === 'done') {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_start.ignored',
              'attempt_start ignored (attempt already resolved)',
              {
                attemptId: typeof message.attempt_id === 'string'
                  ? message.attempt_id
                  : undefined,
                attemptPhase: activeAttemptPhase,
              }
            );
          }

          return;
        }

        const isAttemptAlreadyActive =
          activeAttemptPhase === 'attempt' ||
          activeAttemptPhase === 'countdown';

        if (isAttemptAlreadyActive) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_start.ignored',
              'attempt_start ignored (already active)',
              {
                attemptPhase: activeAttemptPhase,
              }
            );
          }

          return;
        }

        const incomingAttemptId =
          typeof message.attempt_id === 'string' ? message.attempt_id : null;
        const attemptStartIdempotencyKey = getAttemptStartIdempotencyKey(
          message,
          activeSessionId
        );
        const wasAttemptStartAlreadyProcessed = !markAttemptResultProcessed(
          attemptStartIdempotencyKey
        );

        if (wasAttemptStartAlreadyProcessed) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_start.ignored',
              'attempt_start ignored (duplicate idempotency_key)',
              {
                attemptId: incomingAttemptId,
                idempotencyKey: attemptStartIdempotencyKey,
              }
            );
          }

          return;
        }

        const isAwaitingPreviousResult = awaitingResultRef.current;

        if (isAwaitingPreviousResult) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_start.ignored',
              'attempt_start ignored (awaiting result)',
              {
                attemptId: incomingAttemptId,
              }
            );
          }

          return;
        }

        const isDuplicateAttemptId =
          activeAttemptId !== null &&
          incomingAttemptId !== null &&
          incomingAttemptId === activeAttemptId;

        if (isDuplicateAttemptId) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_start.ignored',
              'attempt_start ignored (duplicate)',
              {
                attemptId: incomingAttemptId,
              }
            );
          }

          return;
        }

        const durationSeconds = getAttemptMessageDurationSeconds(message);

        if (durationSeconds === undefined) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_start.invalid',
              'attempt_start missing duration_seconds'
            );
          }

          return;
        }

        const attemptNumber =
          typeof message.attempt_no === 'number' &&
          Number.isFinite(message.attempt_no)
            ? message.attempt_no
            : null;
        const inboundAttemptIndex =
          attemptNumber === null
            ? null
            : Math.min(config.maxAttempts, Math.max(1, attemptNumber + 1));
        const normalizedDurationSeconds = Math.ceil(durationSeconds);
        const staleReason =
          inboundAttemptIndex !== null &&
          inboundAttemptIndex < attemptIndexRef.current
            ? 'stale_attempt_no'
            : normalizedDurationSeconds <= 0
              ? 'non_positive_duration'
              : null;

        if (staleReason) {
          if (wsDebugEnabled) {
            logWarn(
              'teleop.attempt_start.ignored',
              'attempt_start ignored (stale)',
              {
                attemptId: incomingAttemptId,
                attemptNo: attemptNumber,
                durationSeconds,
                inboundAttemptIndex,
                localAttemptIndex: attemptIndexRef.current,
                staleReason,
              }
            );
          }

          const shouldSendStaleAttemptTimeout =
            incomingAttemptId !== null &&
            !staleTimeoutAttemptIdsRef.current.has(incomingAttemptId);

          if (shouldSendStaleAttemptTimeout) {
            const staleAttemptTimeoutSent =
              sendAttemptTimeoutForAttemptRef.current(
                incomingAttemptId,
                'stale_start'
              );

            if (staleAttemptTimeoutSent) {
              staleTimeoutAttemptIdsRef.current.add(incomingAttemptId);
            }
          }

          setReadyMessage('Resyncing attempt');
          setAttemptPhase('waiting');

          return;
        }

        if (attemptNumber !== null && inboundAttemptIndex !== null) {
          const nextAttemptIndex = Math.max(
            attemptIndexRef.current,
            inboundAttemptIndex
          );

          attemptIndexRef.current = nextAttemptIndex;
          setAttemptIndex((previousAttemptIndex) => {
            const updatedAttemptIndex = Math.max(
              previousAttemptIndex,
              inboundAttemptIndex
            );

            return updatedAttemptIndex;
          });
        }

        clearAttemptStartHandoffTimeout();
        beginCountdown(normalizedDurationSeconds, incomingAttemptId);

        if (!incomingAttemptId && wsDebugEnabled) {
          logWarn(
            'teleop.attempt_start.invalid',
            'attempt_start missing attempt_id'
          );
        }

        const existingResultTimeoutId = resultTimeoutRef.current;

        if (existingResultTimeoutId !== null) {
          window.clearTimeout(existingResultTimeoutId);
          resultTimeoutRef.current = null;
        }

        return;
      }

      if (messageType !== 'attempt_result') {
        return;
      }

      const inboundSessionId =
        typeof message.session_id === 'string' ? message.session_id : null;
      const hasMismatchedSession =
        inboundSessionId !== null &&
        inboundSessionId !== activeSessionId;

      if (hasMismatchedSession) {
        if (wsDebugEnabled) {
          logWarn(
            'teleop.attempt_result.ignored',
            'attempt_result ignored (session mismatch)',
            {
              inboundSessionId,
              sessionId: activeSessionId,
            }
          );
        }

        return;
      }

      const hasNoSessionOrAttempt =
        inboundSessionId === null &&
        activeAttemptId === null;

      if (hasNoSessionOrAttempt) {
        if (wsDebugEnabled) {
          logWarn(
            'teleop.attempt_result.ignored',
            'attempt_result ignored (missing session_id and no active attempt)'
          );
        }

        return;
      }

      if (inboundSessionId === null && wsDebugEnabled) {
        logWarn(
          'teleop.attempt_result.unmatched',
          'attempt_result missing session_id; accepting active attempt',
          {
            currentAttemptId: activeAttemptId,
          }
        );
      }

      const inboundAttemptId =
        typeof message.attempt_id === 'string' ? message.attempt_id : null;
      const inboundIdempotencyKey = getAttemptResultIdempotencyKey(message);
      const wasAttemptResultAlreadyProcessed =
        inboundIdempotencyKey !== null &&
        !markAttemptResultProcessed(inboundIdempotencyKey);

      if (wasAttemptResultAlreadyProcessed) {
        if (wsDebugEnabled) {
          logWarn(
            'teleop.attempt_result.ignored',
            'attempt_result ignored (duplicate idempotency_key)',
            {
              idempotencyKey: inboundIdempotencyKey,
            }
          );
        }

        return;
      }

      const hasStaleAttemptId =
        activeAttemptId !== null &&
        inboundAttemptId !== null &&
        inboundAttemptId !== activeAttemptId;

      if (hasStaleAttemptId) {
        if (wsDebugEnabled) {
          logWarn(
            'teleop.attempt_result.ignored',
            'attempt_result ignored (stale attempt_id)',
            {
              currentAttemptId: activeAttemptId,
              inboundAttemptId,
            }
          );
        }

        return;
      }

      const hasAttemptIdWithoutActiveAttempt =
        activeAttemptId === null &&
        inboundAttemptId !== null;

      if (hasAttemptIdWithoutActiveAttempt && wsDebugEnabled) {
        logWarn(
          'teleop.attempt_result.unmatched',
          'attempt_result has attempt_id but no active attempt_id',
          {
            inboundAttemptId,
          }
        );
      }

      const nextAttemptResult = buildAttemptResultFromMessage(message);

      if (nextAttemptResult === null) {
        if (wsDebugEnabled) {
          const rawSuccess = 'success' in message ? message.success : undefined;

          logWarn(
            'teleop.attempt_result.ignored',
            'attempt_result ignored (unparseable success)',
            {
              rawSuccess,
            }
          );
        }

        return;
      }

      showAttemptResult(nextAttemptResult);

      const existingResultTimeoutId = resultTimeoutRef.current;

      if (existingResultTimeoutId !== null) {
        window.clearTimeout(existingResultTimeoutId);
        resultTimeoutRef.current = null;
      }

      clearAttemptStartHandoffTimeout();
      clearCountdownTimers();
      pendingAttemptRef.current = null;
      setCountdownValue(null);
      setAwaitingResult(false);
      awaitingResultRef.current = false;
      setCurrentAttemptIdWithRef(null);
      setReadyMessage(null);
      setPhaseEndsAt(null);
      setSecondsLeft(0);
      switchToOverhead();

      const reachedFinalAttempt =
        nextAttemptResult.success || attemptIndexRef.current >= config.maxAttempts;

      if (reachedFinalAttempt) {
        setBonusAttemptPrompt(null);
        setPostAttemptPrompt({
          success: nextAttemptResult.success,
          reason: nextAttemptResult.reason,
          timestamp: nextAttemptResult.timestamp,
        });
        setAttemptPhase('done');

        return;
      }

      setPostAttemptPrompt(null);
      setBonusAttemptPrompt({
        nextAttempt: Math.min(attemptIndexRef.current + 1, config.maxAttempts),
        totalAttempts: config.maxAttempts,
      });
      setAttemptPhase('waiting');
      setAttemptIndex((previousAttemptIndex) => {
        const nextAttemptIndex = Math.min(
          previousAttemptIndex + 1,
          config.maxAttempts
        );

        attemptIndexRef.current = nextAttemptIndex;

        return nextAttemptIndex;
      });
    });

    return () => {
      removeListener();
    };
  }, [
    addMessageListener,
    beginCountdown,
    clearAttemptStartHandoffTimeout,
    clearCountdownTimers,
    config.attemptWaitEnqueuedReason,
    config.maxAttempts,
    markAttemptResultProcessed,
    scheduleAttemptStartHandoffRecovery,
    setCurrentAttemptIdWithRef,
    showAttemptResult,
    switchToOverhead,
  ]);

  useEffect(() => {
    return () => {
      const attemptTimeoutId = attemptTimeoutRef.current;

      if (attemptTimeoutId !== null) {
        window.clearTimeout(attemptTimeoutId);
        attemptTimeoutRef.current = null;
      }

      const resultTimeoutId = resultTimeoutRef.current;

      if (resultTimeoutId !== null) {
        window.clearTimeout(resultTimeoutId);
        resultTimeoutRef.current = null;
      }

      const bonusPromptTimeoutId = bonusPromptTimeoutRef.current;

      if (bonusPromptTimeoutId !== null) {
        window.clearTimeout(bonusPromptTimeoutId);
        bonusPromptTimeoutRef.current = null;
      }

      clearAttemptStartHandoffTimeout();
      clearCountdownTimers();
    };
  }, [clearAttemptStartHandoffTimeout, clearCountdownTimers]);

  useEffect(() => {
    const existingBonusPromptTimeoutId = bonusPromptTimeoutRef.current;

    if (existingBonusPromptTimeoutId !== null) {
      window.clearTimeout(existingBonusPromptTimeoutId);
      bonusPromptTimeoutRef.current = null;
    }

    if (!bonusAttemptPrompt) {
      return;
    }

    bonusPromptTimeoutRef.current = window.setTimeout(() => {
      bonusPromptTimeoutRef.current = null;
      finalizeBonusTimeout();
    }, config.bonusPromptTimeoutMs);

    return () => {
      const activeBonusPromptTimeoutId = bonusPromptTimeoutRef.current;

      if (activeBonusPromptTimeoutId === null) {
        return;
      }

      window.clearTimeout(activeBonusPromptTimeoutId);
      bonusPromptTimeoutRef.current = null;
    };
  }, [bonusAttemptPrompt, config.bonusPromptTimeoutMs, finalizeBonusTimeout]);

  useEffect(() => {
    const hasResolvedPrompt =
      postAttemptPrompt !== null ||
      bonusAttemptPrompt !== null;
    if (!connected) {
      return;
    }

    if (hasResolvedPrompt) {
      return;
    }

    const hasSentReady = readySentRef.current;

    if (hasSentReady) {
      return;
    }

    if (!sessionId) {
      return;
    }

    const sent = sendOperatorReady(sessionId);

    if (!sent) {
      return;
    }

    setReadyMessage('Robot getting ready');
  }, [bonusAttemptPrompt, connected, postAttemptPrompt, sendOperatorReady, sessionId]);

  useEffect(() => {
    const shouldSkipRetryLoop =
      !connected ||
      !sessionId ||
      postAttemptPrompt !== null ||
      bonusAttemptPrompt !== null ||
      attemptPhase !== 'waiting' ||
      awaitingResult;

    if (shouldSkipRetryLoop) {
      return;
    }

    const intervalId = window.setInterval(() => {
      const shouldSkipRetrySend =
        postAttemptPromptRef.current !== null ||
        bonusAttemptPromptRef.current !== null ||
        attemptPhaseRef.current !== 'waiting' ||
        awaitingResultRef.current ||
        readyMessageRef.current === ROBOT_READY_MESSAGE;

      if (shouldSkipRetrySend) {
        return;
      }

      const sent = sendOperatorReady(sessionId);

      if (!sent) {
        return;
      }

      setReadyMessage((previousReadyMessage) => {
        const nextReadyMessage =
          previousReadyMessage ?? 'Robot getting ready';

        return nextReadyMessage;
      });
    }, 2000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    attemptPhase,
    awaitingResult,
    bonusAttemptPrompt,
    connected,
    postAttemptPrompt,
    sendOperatorReady,
    sessionId,
  ]);

  useEffect(() => {
    if (!phaseEndsAt) {
      return;
    }

    if (attemptPhase !== 'attempt') {
      return;
    }

    let timeoutId: number | null = null;

    const tick = () => {
      const now = Date.now();
      const remainingSeconds = Math.max(
        0,
        Math.ceil((phaseEndsAt - now) / 1000)
      );

      setSecondsLeft(remainingSeconds);

      if (remainingSeconds <= 0) {
        triggerGrab();
      }

      if (phaseEndsAt !== null) {
        timeoutId = window.setTimeout(tick, 200);
      }
    };

    tick();

    return () => {
      if (timeoutId === null) {
        return;
      }

      window.clearTimeout(timeoutId);
    };
  }, [attemptPhase, phaseEndsAt, triggerGrab]);

  const controlsEnabled = attemptPhase === 'attempt' && !awaitingResult;
  const attemptState = {
    attemptResult,
    postAttemptPrompt,
    bonusAttemptPrompt,
    attemptPhase,
    attemptIndex,
    secondsLeft,
    awaitingResult,
    readyMessage,
    countdownValue,
    lastWsMeta,
    sessionEventLog,
    controlsEnabled,
  };

  return {
    ...attemptState,
    handlePlayAgain,
    handleBonusAttemptOk,
    triggerGrab,
  };
};
